import { InfoCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import {
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  Radio,
  Row,
  Select,
  Tooltip,
  message,
} from 'antd';
import type { FormInstance } from 'antd';
import generator from 'generate-password';
import moment from 'moment';
import { FC, useCallback, useEffect, useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useParams } from 'react-router-dom';
import {
  fetchGroupsWithDescription,
  fetchUserGroups,
} from '../../../api/groups';
import { fetchUsers } from '../../../api/users';
import {
  AddUserFormInterface,
  EditUserFormInterface,
  SelectItem,
  UserDetails,
} from '../../../constants/types';
import theme from '../../../styles/theme';
import { getParentOrigin } from '../../../utils/getKeycloakConfig';
import {
  CheckboxContainer,
  Note,
  StyledCheckboxGroup,
  StyledCheckboxItem,
  StyledFormItem,
} from '../style';
import AccessExpiration from './AccessExpiration';
import { validateEmail } from './validators';

const { Option } = Select;

interface Props {
  form: FormInstance;
  type: 'add' | 'edit';
  alreadyDeactivatedModalShown?: (value: boolean) => void;
  userDetails?: UserDetails;
  isFormSubmit: boolean;
  formSubmit: (
    values: AddUserFormInterface | EditUserFormInterface,
  ) => Promise<void>;
}

const UserForm: FC<Props> = ({
  type,
  form,
  alreadyDeactivatedModalShown,
  userDetails,
  isFormSubmit,
  formSubmit,
}) => {
  const intl = useIntl();
  const parentOrigin = getParentOrigin();
  const history = useHistory();
  const { type: reactiveUserFlow } = useParams<{ type: string }>();
  const [users, setUsers] = useState<SelectItem[]>([]);
  const [groups, setGroups] = useState<any>([]);
  const [tenantAdminGroupId, setTenantAdminGroupId] = useState<
    string | undefined
  >(undefined);
  const [isUsersLoading, setIsUsersLoading] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const { setFieldsValue } = form;

  const loadGroups = useCallback(() => {
    fetchGroupsWithDescription().then(groups => {
      const groupsListDetails = groups.map(group => ({
        value: group.id,
        label: group.name,
        description: group.description,
      }));
      const tenantAdminGroup = groupsListDetails.find(
        item => item.label === 'tenant-admin',
      );
      if (tenantAdminGroup) {
        setTenantAdminGroupId(tenantAdminGroup.value);
      }
      setGroups(groupsListDetails);
    });
  }, [setGroups]);

  const loadUsers = useCallback(() => {
    setIsUsersLoading(true);
    fetchUsers()
      .then(users => {
        const userListDetails = users
          .filter(
            (user: UserDetails) =>
              user.enabled !== false &&
              (user?.['attributes']?.['account_end_date']?.[0]
                ? moment(
                    parseInt(user['attributes']['account_end_date'][0], 10),
                  ).valueOf() > moment().valueOf()
                : true),
          )
          .map((user: UserDetails) => ({
            value: user.id,
            label: `${user.firstName} ${user.lastName} ${
              user.email ? `| ${user.email}` : ''
            }`,
          })) as SelectItem[];
        setUsers(userListDetails);
      })
      .finally(() => {
        setIsUsersLoading(false);
      });
  }, [setIsUsersLoading]);

  const onCopyUserGroups = useCallback(
    (userId: string) => {
      fetchUserGroups(userId).then(groups => {
        const userGroups: string[] = groups.map((group: any) => group.id);
        setFieldsValue({
          groups: userGroups,
        });
      });
    },
    [setFieldsValue],
  );

  useEffect(() => {
    loadUsers();
    loadGroups();
    return () => {
      setGroups([]);
      setUsers([]);
    };
  }, [loadGroups, loadUsers]);

  useEffect(() => {
    if (type === 'edit' && userDetails) {
      onCopyUserGroups(`${userDetails.id}`);
      const currentUserDetails: any = {
        firstName: userDetails?.firstName,
        lastName: userDetails?.lastName,
        email: userDetails?.email,
        configure_totp:
          userDetails?.requiredActions?.includes('CONFIGURE_TOTP') ||
          userDetails?.totp,
      };

      if (
        userDetails?.attributes?.account_end_date?.length &&
        reactiveUserFlow !== 'reactiveUser'
      ) {
        const aed =
          Number(userDetails?.attributes?.account_end_date[0]) || false;
        if (aed) {
          currentUserDetails['account_end_date'] = moment(aed);
        }
      }

      form.setFieldsValue({
        ...currentUserDetails,
      });
    }
  }, [form, onCopyUserGroups, type, reactiveUserFlow, userDetails]);

  const checkEmail = async (rule: any, value: string): Promise<void> => {
    if (type === 'add') {
      return validateEmail(rule, value).catch(error => {
        if (error.message === 'User.colum.deactivateUser') {
          if (alreadyDeactivatedModalShown) {
            alreadyDeactivatedModalShown(true);
          }
        } else {
          throw new Error(
            intl.formatMessage({ id: error.message }, { email: value }),
          );
        }
      });
    } else if (type === 'edit') {
      if (userDetails?.email !== value) {
        return validateEmail(rule, value).catch(error => {
          throw new Error(
            intl.formatMessage(
              {
                id: error.message,
              },
              { email: value },
            ),
          );
        });
      }
    }
    return Promise.resolve();
  };

  const onGeneratePassword = () => {
    const generatedPassword = generator.generate({
      length: 10,
      numbers: true,
      symbols: true,
      strict: true,
    });
    setFieldsValue({
      password: generatedPassword,
    });
    setGeneratedPassword(generatedPassword);
  };

  const onCopyPassword = () => {
    message.success(
      intl.formatMessage({
        id: 'User.form.validation.passwordCopied',
      }),
      1,
    );
  };

  const backToUsersPage = () => {
    if (parentOrigin && window.top && window.top !== window) {
      window.top.postMessage(
        {
          type: 'redirect',
          url: '/admin/users',
        },
        parentOrigin,
      );
    } else {
      history.push('/users');
    }
  };

  return (
    <>
      <Form
        layout="vertical"
        onFinish={formSubmit}
        validateTrigger="onBlur"
        form={form}
        data-testid="user-form"
        preserve={false}
        autoComplete="off"
      >
        <Row gutter={15}>
          <Col span={12}>
            <StyledFormItem
              label={<FormattedMessage id="User.form.label.firstName" />}
              name="firstName"
              rules={[
                {
                  whitespace: true,
                  message: intl.formatMessage({
                    id: 'User.form.validation.firstName',
                  }),
                },
                {
                  required: true,
                  message: intl.formatMessage({
                    id: 'User.form.validation.firstName',
                  }),
                },
              ]}
            >
              <Input data-testid="user-firstname" />
            </StyledFormItem>
          </Col>
          <Col span={12}>
            <StyledFormItem
              label={<FormattedMessage id="User.form.label.lastName" />}
              name="lastName"
              rules={[
                {
                  whitespace: true,
                  message: intl.formatMessage({
                    id: 'User.form.validation.lastName',
                  }),
                },
                {
                  required: true,
                  message: intl.formatMessage({
                    id: 'User.form.validation.lastName',
                  }),
                },
              ]}
            >
              <Input data-testid="user-lastname" />
            </StyledFormItem>
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            <StyledFormItem
              label={<FormattedMessage id="User.form.label.email" />}
              name="email"
              validateFirst
              normalize={value => (value || '').toLowerCase()}
              rules={[
                {
                  whitespace: true,
                  message: intl.formatMessage({
                    id: 'User.form.validation.email',
                  }),
                },
                {
                  required: true,
                  message: intl.formatMessage({
                    id: 'User.form.validation.email',
                  }),
                },
                {
                  type: 'email',
                  message: intl.formatMessage({
                    id: 'User.form.validation.validEmailAddress',
                  }),
                },
                { validator: checkEmail },
              ]}
            >
              <Input data-testid="user-email" disabled={type === 'edit'} />
            </StyledFormItem>
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            <StyledFormItem
              label={
                <FormattedMessage id="User.form.label.selectGroupOption" />
              }
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: 'User.form.chooseoneGroup',
                  }),
                },
              ]}
              required
              name="userGroupsWay"
              initialValue="selectUserGroups"
            >
              <Radio.Group
                onChange={() => {
                  if (type === 'add') {
                    setFieldsValue({
                      groups: [],
                    });
                  } else if (type === 'edit') {
                    if (userDetails?.id) {
                      onCopyUserGroups(userDetails.id);
                    }
                  }
                }}
              >
                <Radio
                  data-testid="user-select-copygroups"
                  value="copyUserGroups"
                >
                  <FormattedMessage id="User.form.label.applyExistingUser" />
                </Radio>
                <Radio
                  data-testid="user-select-usergroups"
                  value="selectUserGroups"
                >
                  <FormattedMessage id="User.form.label.selectUserGroups" />
                </Radio>
              </Radio.Group>
            </StyledFormItem>
          </Col>
        </Row>

        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.userGroupsWay !== currentValues.userGroupsWay
          }
        >
          {({ getFieldValue }) => {
            if (getFieldValue('userGroupsWay') === 'copyUserGroups') {
              return (
                <Row>
                  <Col
                    span={24}
                    style={{
                      marginBottom: '15px',
                      marginTop: '-10px',
                    }}
                  >
                    <Select
                      showSearch
                      optionFilterProp="children"
                      showArrow
                      allowClear
                      loading={isUsersLoading}
                      placeholder={<FormattedMessage id="User.filter.search" />}
                      onChange={(userId: string) =>
                        userId && onCopyUserGroups(userId)
                      }
                      filterOption={(input, option) =>
                        option?.children
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                      }
                      style={{ width: '100%' }}
                    >
                      {users.map((user, index) => (
                        <Option key={index} value={user.value}>
                          {user.label}
                        </Option>
                      ))}
                    </Select>
                  </Col>
                </Row>
              );
            }
            return null;
          }}
        </Form.Item>

        <Row>
          <Col span={24}>
            <CheckboxContainer>
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.userGroupsWay !== currentValues.userGroupsWay
                }
              >
                {({ getFieldValue }) => {
                  return (
                    <StyledFormItem
                      data-testid="user-usergroups"
                      style={{ marginBottom: '0' }}
                      name="groups"
                    >
                      <StyledCheckboxGroup className="without-boarder">
                        {groups.map((group: any, index: number) => (
                          <StyledCheckboxItem key={index}>
                            <Checkbox
                              disabled={
                                getFieldValue('userGroupsWay') ===
                                'copyUserGroups'
                              }
                              value={group.value}
                              data-testid={`user-usergroup-${index}`}
                            >
                              {group.label}
                            </Checkbox>
                            {group.description.length > 0 && (
                              <Tooltip title={group.description}>
                                <InfoCircleOutlined
                                  style={{ color: theme.palette.primary.main }}
                                />
                              </Tooltip>
                            )}
                          </StyledCheckboxItem>
                        ))}
                      </StyledCheckboxGroup>
                    </StyledFormItem>
                  );
                }}
              </Form.Item>
            </CheckboxContainer>
          </Col>
        </Row>

        <StyledFormItem
          name="configure_totp"
          valuePropName="checked"
          className="mbi-0 mt-8"
          label={
            <span>
              <b className="mr-4">
                <FormattedMessage id="User.form.label.configure_totp" />
              </b>
              <Tooltip
                placement="top"
                overlayClassName="time-tooltip"
                autoAdjustOverflow={false}
                title={
                  <FormattedMessage id="User.form.label.configure_totp.tooltip" />
                }
              >
                <QuestionCircleOutlined className="grey-icon" />
              </Tooltip>
            </span>
          }
        >
          <Checkbox>
            <FormattedMessage id="User.form.label.configure_totp.checkbox" />
          </Checkbox>
        </StyledFormItem>

        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.groups !== currentValues.groups
          }
        >
          {({ getFieldValue }) => {
            const value = getFieldValue('groups');
            if (
              value &&
              Array.isArray(value) &&
              value.length > 0 &&
              value.includes(tenantAdminGroupId)
            ) {
              return null;
            }
            return (
              <AccessExpiration
                isAccountEnddateExist={
                  (userDetails?.attributes?.account_end_date?.length &&
                    reactiveUserFlow !== 'reactiveUser') ||
                  false
                }
              />
            );
          }}
        </Form.Item>

        {type === 'add' && (
          <>
            <Row>
              <Col span={24}>
                <StyledFormItem
                  label={
                    <FormattedMessage id="User.form.label.addUserOption" />
                  }
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({
                        id: 'User.form.chooseoneGroup',
                      }),
                    },
                  ]}
                  required
                  name="createCredentialsOrSendInvitation"
                  initialValue="sendInvitationLink"
                  className="mbi-0 mt-12"
                >
                  <Radio.Group onChange={() => setGeneratedPassword('')}>
                    <Radio
                      value="sendInvitationLink"
                      data-testid="user-sendinvite"
                    >
                      <FormattedMessage id="User.form.radio.invitationLink" />
                    </Radio>
                    <Radio
                      value="createCredentials"
                      data-testid="user-withcredentials"
                    >
                      <FormattedMessage id="User.form.radio.createCredentials" />
                    </Radio>
                  </Radio.Group>
                </StyledFormItem>
              </Col>
            </Row>

            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.createCredentialsOrSendInvitation !==
                currentValues.createCredentialsOrSendInvitation
              }
            >
              {({ getFieldValue }) => {
                if (
                  getFieldValue('createCredentialsOrSendInvitation') ===
                  'createCredentials'
                ) {
                  return (
                    <Row gutter={[16, 8]}>
                      <Col span={14}>
                        <StyledFormItem
                          label={
                            <>
                              <FormattedMessage id="User.form.password.label" />
                              <Tooltip
                                title={
                                  <FormattedMessage id="User.tooltip.generatePassword" />
                                }
                              >
                                <InfoCircleOutlined />
                              </Tooltip>
                            </>
                          }
                          name="password"
                          className="mbi-0 pl-24"
                          rules={[
                            {
                              required: true,
                              message: intl.formatMessage({
                                id: 'User.form.validation.password',
                              }),
                            },
                          ]}
                        >
                          <Input.Password
                            onChange={() => setGeneratedPassword('')}
                            data-testid="user-password"
                          />
                        </StyledFormItem>
                      </Col>
                      <Col span={10}>
                        <Row gutter={[8, 8]}>
                          <Col>
                            <Button
                              style={{ marginTop: '22px' }}
                              block
                              onClick={onGeneratePassword}
                              type="primary"
                              shape="round"
                              className="mwi-100"
                              data-testid="user-password-generate"
                            >
                              <FormattedMessage id="User.form.button.generate" />
                            </Button>
                          </Col>
                          <Col>
                            <CopyToClipboard
                              onCopy={onCopyPassword}
                              text={generatedPassword}
                            >
                              <Button
                                style={{ marginTop: '22px' }}
                                block
                                shape="round"
                                className="mwi-100"
                                disabled={!generatedPassword}
                                type="default"
                                data-testid="user-password-copy"
                              >
                                <FormattedMessage id="Global.copy" />
                              </Button>
                            </CopyToClipboard>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  );
                }
                return null;
              }}
            </Form.Item>
          </>
        )}

        {type === 'edit' && (
          <Row className="mt-12">
            <Col span={24}>
              <StyledFormItem
                label={<FormattedMessage id="User.form.radio.invitationLink" />}
                name="sendResetLink"
                valuePropName="checked"
              >
                <Checkbox data-testid="user-sendresetlink">
                  <FormattedMessage id="User.form.radio.invitationLink" />
                </Checkbox>
              </StyledFormItem>
            </Col>
          </Row>
        )}

        <Row className="mt-12">
          <Col span={24}>
            <Note>
              <span className="required">*</span>
              <FormattedMessage id="ApiKeys.form.label.cumpulsoryFields" />
            </Note>
          </Col>
        </Row>

        <Row gutter={[8, 8]} justify="center" className="mt-12">
          <Col>
            <Button
              shape="round"
              size="large"
              onClick={backToUsersPage}
              type="default"
              data-testid="user-cancel"
            >
              <FormattedMessage id="Global.cancel" />
            </Button>
          </Col>
          <Col>
            <Button
              type="primary"
              htmlType="submit"
              disabled={isFormSubmit}
              shape="round"
              size="large"
              data-testid="user-submit"
            >
              <FormattedMessage id="User.form.button.submit" />
            </Button>
          </Col>
        </Row>
      </Form>
    </>
  );
};

export default UserForm;
